import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import Page from '@/components/Page';
import Card from '@/components/Card';
import LoadingState from '@/components/LoadingState';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import Chip from '@/components/Chip';
import Button from '@/components/Button';
import Alert from '@/components/Alert';
import { DocumentsAPI } from '@/services/documentsAPI';
import { Document, DocumentVersion, DocumentAudit } from '@/types/documents';
import { formatFieldCounts } from '@/utils/pdfFieldConfig';
import ROUTES from '@/routes/routes';
import UploadVersionModal from './UploadVersionModal';

const TAX_TYPE_LABELS: Record<string, string> = {
  all: 'All Workers',
  w2: 'W-2 Only',
  '1099': '1099 Only',
};

const ACTION_COLORS: Record<
  string,
  'success' | 'warning' | 'danger' | undefined
> = {
  created: 'success',
  updated: undefined,
  deactivated: 'danger',
  reactivated: 'success',
  version_uploaded: 'success',
  reordered: undefined,
  signature_position_set: 'success',
};

const formatDate = (dateString?: string) => {
  if (!dateString) return 'N/A';
  try {
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
  } catch {
    return dateString;
  }
};

const formatChanges = (
  change_details: Record<string, unknown> | null,
): string => {
  if (!change_details) return '';
  return Object.entries(change_details)
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        return `${key}: ${value[0]} → ${value[1]}`;
      }
      return `${key}: ${JSON.stringify(value)}`;
    })
    .join(', ');
};

const DocumentShow: React.FC = () => {
  const navigate = useNavigate();
  const { documentId } = useParams<{ documentId: string }>();

  const [loading, setLoading] = useState(true);
  const [doc, setDoc] = useState<Document | null>(null);
  const [versions, setVersions] = useState<DocumentVersion[]>([]);
  const [audits, setAudits] = useState<DocumentAudit[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(true);
  const [auditsLoading, setAuditsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploadDoc, setUploadDoc] = useState<Document | null>(null);

  const fetchDocument = useCallback(async () => {
    if (!documentId) return;
    setLoading(true);
    try {
      const response = await DocumentsAPI.getDocuments();
      if (response.success) {
        const found = response.data.find((d) => d.id === Number(documentId));
        if (found) {
          setDoc(found);
        } else {
          setError('Document not found');
        }
      }
    } catch {
      setError('Failed to load document');
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  const fetchVersions = useCallback(async () => {
    if (!documentId) return;
    setVersionsLoading(true);
    try {
      const response = await DocumentsAPI.getVersions(Number(documentId));
      if (response.success) {
        setVersions(response.data);
      }
    } catch {
      // silently fail, versions section will show empty
    } finally {
      setVersionsLoading(false);
    }
  }, [documentId]);

  const fetchAudits = useCallback(async () => {
    if (!documentId) return;
    setAuditsLoading(true);
    try {
      const response = await DocumentsAPI.getAudits(Number(documentId));
      if (response.success) {
        setAudits(response.data);
      }
    } catch {
      // silently fail
    } finally {
      setAuditsLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    fetchDocument();
    fetchVersions();
    fetchAudits();
  }, [fetchDocument, fetchVersions, fetchAudits]);

  const canActivate = (d: Document) => {
    return d.latest_version?.has_signature_coordinates === true;
  };

  const handleToggleActive = async () => {
    if (!doc) return;
    if (!doc.active && !canActivate(doc)) {
      setError(
        'Cannot activate: signature position must be set on the latest version first.',
      );
      setTimeout(() => setError(null), 5000);
      return;
    }
    try {
      await DocumentsAPI.updateDocument(doc.id, { active: !doc.active });
      setSuccess(
        `Document ${doc.active ? 'deactivated' : 'reactivated'} successfully`,
      );
      fetchDocument();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(
        err.response?.data?.errors?.join(', ') || 'Failed to update document',
      );
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleDelete = async () => {
    if (!doc) return;
    const confirmed = window.confirm(
      `Are you sure you want to delete "${doc.name}"? This action can be undone by an engineer if needed.`,
    );
    if (!confirmed) return;

    try {
      await DocumentsAPI.deleteDocument(doc.id);
      setSuccess(`Document "${doc.name}" deleted successfully`);
      setTimeout(() => navigate(`/${ROUTES.documents}`), 1500);
    } catch (err: any) {
      setError(
        err.response?.data?.errors?.join(', ') || 'Failed to delete document',
      );
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleUploadSuccess = () => {
    setUploadDoc(null);
    setSuccess('New version uploaded successfully');
    fetchDocument();
    fetchVersions();
    fetchAudits();
    setTimeout(() => setSuccess(null), 3000);
  };

  if (loading) {
    return (
      <Page title="Document Details">
        <Card>
          <LoadingState />
        </Card>
      </Page>
    );
  }

  if (!doc) {
    return (
      <Page
        breadcrumbs={[
          {
            label: 'Documents',
            to: `/${ROUTES.documents}`,
            a11yLabel: 'Back to documents',
          },
        ]}
        title="Document Not Found"
      >
        <Card>
          <Card.Section>
            <Body>This document could not be found.</Body>
          </Card.Section>
        </Card>
      </Page>
    );
  }

  return (
    <Page
      breadcrumbs={[
        {
          label: 'Documents',
          to: `/${ROUTES.documents}`,
          a11yLabel: 'Back to documents',
        },
      ]}
      size="full"
      title={doc.name}
    >
      {error && (
        <Alert
          description={error}
          status="danger"
          title="Error"
          onDismiss={() => setError(null)}
        />
      )}

      {success && (
        <Alert
          description={success}
          status="success"
          title="Success"
          onDismiss={() => setSuccess(null)}
        />
      )}

      {/* Document Details */}
      <Card>
        <Card.Header title="Details" />
        <Card.Section>
          <Stack gap={32}>
            <Stack gap={4} vertical>
              <Small color="lighter">Status</Small>
              <div>
                {doc.active ? (
                  <Chip color="success" label="Active" size="small" />
                ) : (
                  <Chip label="Inactive" size="small" />
                )}
              </div>
            </Stack>
            <Stack gap={4} vertical>
              <Small color="lighter">Tax Type</Small>
              <Body>{TAX_TYPE_LABELS[doc.tax_type] || doc.tax_type}</Body>
            </Stack>
            <Stack gap={4} vertical>
              <Small color="lighter">States</Small>
              <Body>{doc.states?.join(', ') || 'ALL'}</Body>
            </Stack>
            <Stack gap={4} vertical>
              <Small color="lighter">Current Version</Small>
              <Body>
                v{doc.latest_version?.version_number || 0} ({doc.versions_count}{' '}
                total)
              </Body>
            </Stack>
            <Stack gap={4} vertical>
              <Small color="lighter">Last Updated</Small>
              <Body>{formatDate(doc.updated_at)}</Body>
            </Stack>
          </Stack>
        </Card.Section>
        <Card.Section subdued>
          <Stack gap={8} justify="apart">
            <Stack gap={8}>
              <Button
                a11yLabel="Edit document"
                appearance="outline"
                label="Edit"
                onClick={() => navigate(`/${ROUTES.documents}/${doc.id}/edit`)}
              />
              <Button
                a11yLabel={
                  doc.active ? 'Deactivate document' : 'Activate document'
                }
                appearance="outline"
                label={doc.active ? 'Deactivate' : 'Activate'}
                disabled={!doc.active && !canActivate(doc)}
                onClick={handleToggleActive}
              />
              <Button
                a11yLabel="Upload new version"
                appearance="outline"
                label="Upload Version"
                onClick={() => setUploadDoc(doc)}
              />
              {canActivate(doc) && (
                <Button
                  a11yLabel="Test sign document"
                  appearance="outline"
                  label="Test Sign"
                  onClick={() =>
                    navigate(`/${ROUTES.documents}/${doc.id}/test-sign`)
                  }
                />
              )}
            </Stack>
            <Button
              a11yLabel="Delete document"
              appearance="outline"
              label="Delete"
              onClick={handleDelete}
            />
          </Stack>
        </Card.Section>
      </Card>

      {/* Version History */}
      <div style={{ marginTop: '24px' }}>
        <Card>
          <Card.Header title="Version History" />
          <Card.Section>
            {versionsLoading ? (
              <LoadingState />
            ) : versions.length === 0 ? (
              <Body>No versions found.</Body>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #ddd' }}>
                    <th style={{ padding: '8px', textAlign: 'left' }}>
                      Version
                    </th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>
                      Signature Position
                    </th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>
                      Uploaded
                    </th>
                    <th style={{ padding: '8px', textAlign: 'right' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {versions.map((version) => (
                    <tr
                      key={version.id}
                      style={{ borderBottom: '1px solid #eee' }}
                    >
                      <td style={{ padding: '8px' }}>
                        <Body weight="medium">v{version.version_number}</Body>
                      </td>
                      <td style={{ padding: '8px' }}>
                        {version.has_signature_coordinates ? (
                          <Stack gap={4} vertical>
                            <Chip color="success" label="Set" size="small" />
                            <Small>
                              {version.signature_fields?.length > 0 ? (
                                formatFieldCounts(version.signature_fields)
                              ) : (
                                <>
                                  Page {version.signature_page}, (
                                  {version.signature_x?.toFixed(1)},{' '}
                                  {version.signature_y?.toFixed(1)})
                                </>
                              )}
                            </Small>
                          </Stack>
                        ) : (
                          <Chip label="Not set" size="small" />
                        )}
                      </td>
                      <td style={{ padding: '8px' }}>
                        <Small>{formatDate(version.created_at)}</Small>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>
                        <Button
                          a11yLabel={`Download version ${version.version_number}`}
                          appearance="outline"
                          label="Download"
                          onClick={() => {
                            if (version.download_url) {
                              window.open(version.download_url, '_blank');
                            }
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card.Section>
        </Card>
      </div>

      {/* Audit Log */}
      <div style={{ marginTop: '24px' }}>
        <Card>
          <Card.Header title="Audit Log" />
          <Card.Section>
            {auditsLoading ? (
              <LoadingState />
            ) : audits.length === 0 ? (
              <Body>No audit entries found.</Body>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #ddd' }}>
                    <th style={{ padding: '8px', textAlign: 'left' }}>
                      Action
                    </th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>
                      Details
                    </th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Admin</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>IP</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {audits.map((audit) => (
                    <tr
                      key={audit.id}
                      style={{ borderBottom: '1px solid #eee' }}
                    >
                      <td style={{ padding: '8px' }}>
                        <Chip
                          color={ACTION_COLORS[audit.action]}
                          label={audit.action}
                          size="small"
                        />
                      </td>
                      <td style={{ padding: '8px', maxWidth: '300px' }}>
                        <Small>{formatChanges(audit.change_details)}</Small>
                      </td>
                      <td style={{ padding: '8px' }}>
                        <Small>
                          {audit.admin_name || `ID: ${audit.admin_user_id}`}
                        </Small>
                      </td>
                      <td style={{ padding: '8px' }}>
                        <Small>{audit.ip_address || 'N/A'}</Small>
                      </td>
                      <td style={{ padding: '8px' }}>
                        <Small>{formatDate(audit.created_at)}</Small>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card.Section>
        </Card>
      </div>

      {uploadDoc && (
        <UploadVersionModal
          document={uploadDoc}
          onClose={() => setUploadDoc(null)}
          onSuccess={handleUploadSuccess}
        />
      )}
    </Page>
  );
};

export default DocumentShow;
